// How Insights writes a date — one way, everywhere:
// - a day: "Sep 17, 2026" (the backend writes lesson dates the same way);
// - a range: "Aug 1 – Oct 16, 2026", the year once when both ends share it;
// - inside a chart (slider handles, points, the month axis): "Sep 17", "Sep",
//   with "Jan 2026" where the year turns — the text around a chart carries it.
//
// A value is either a calendar day ('YYYY-MM-DD', read in local time) or an
// instant (a full ISO timestamp or a Date). Callers keep their own reading of
// an instant: `utc` shows the day it falls on in UTC, which is the day the
// backend counts it under.

type DayValue = string | Date;
type Opts = { utc?: boolean };

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

// A 'YYYY-MM-DD' calendar day as a local Date, at noon so no time zone or DST
// change moves it to a neighbouring day. `new Date(iso)` would read it as UTC
// midnight — the previous day for every US viewer.
export function localDay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

const toDate = (value: DayValue): Date =>
  value instanceof Date
    ? value
    : CALENDAR_DAY.test(value)
    ? localDay(value)
    : new Date(value);

// A calendar day is already its own day: never shift it to UTC.
const zone = (value: DayValue, opts?: Opts) =>
  opts?.utc && !(typeof value === 'string' && CALENDAR_DAY.test(value))
    ? { timeZone: 'UTC' }
    : {};

export function formatDay(value: DayValue, opts?: Opts): string {
  return toDate(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...zone(value, opts),
  });
}

// "Aug 1 – Oct 16, 2026"; across years "Dec 2, 2025 – Jan 9, 2026"; one day
// "Aug 1, 2026". A `joiner` of ' and ' fits it into a sentence: "between Aug 1
// and Oct 16, 2026".
export function formatDayRange(
  from: DayValue,
  to: DayValue,
  opts?: Opts & { joiner?: string },
): string {
  const f = formatDay(from, opts);
  const t = formatDay(to, opts);
  if (f === t) return f;
  const [fDay, fYear] = f.split(', ');
  const [, tYear] = t.split(', ');
  const joiner = opts?.joiner ?? ' – ';
  return `${fYear === tYear ? fDay : f}${joiner}${t}`;
}

// Inside a chart only.
export function formatChartDay(value: DayValue, opts?: Opts): string {
  return toDate(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...zone(value, opts),
  });
}

// A month on a chart's axis: "Sep", or "Jan 2026" where the year should show.
export function formatChartMonth(value: DayValue, withYear = false): string {
  return toDate(value).toLocaleDateString('en-US', {
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}
